import { mdiAlertCircle, mdiCheckCircle, mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import _ from 'lodash';
import * as React from 'react';
import { validateLatin1String } from '../../../utils';
import { useLatin1ValidationContext } from '../../contexts/latin1-validation-context';
import * as S from './input.styles';

export type IBaseInputProps = Omit<React.PropsWithoutRef<JSX.IntrinsicElements['input']>, 'children'> & {
    successMessage?: string;
    errorMessage?: string;
    /**
     * If property is passed, then we show remove button on the right
     */
    onDelete?: () => void;
    /**
     * Custom postfix button with handler on the right.
     * optionPath is an SVG path string
     */
    onOption?: (e: React.MouseEvent) => void;
    optionPath?: string;
    /**
     * If true, disables Latin-1 character validation for this input.
     * By default, all inputs validate for Latin-1 encoded characters on blur.
     */
    disableLatin1Validation?: boolean;
};

export const BaseInput: React.FunctionComponent<IBaseInputProps> = (props) => {
    const { successMessage, errorMessage, onDelete, onOption, optionPath, required, capture, disableLatin1Validation, onBlur, name, placeholder, ...restProps } = props;
    const { value, readOnly } = restProps;
    
    const [latin1ValidationError, setLatin1ValidationError] = React.useState<string | null>(null);
    const { registerError } = useLatin1ValidationContext();
    const inputIdRef = React.useRef<string>(`input-${Math.random().toString(36).substr(2, 9)}`);

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
        if (!disableLatin1Validation) {
            const value = e.target.value;
            const validationResult = validateLatin1String(value);
            const error = validationResult.isValid ? null : validationResult.errorMessage || null;
            
            setLatin1ValidationError(error);
            
            // Register error with context using field name (placeholder or name)
            const fieldName = name || placeholder || 'Input field';
            registerError(inputIdRef.current, fieldName, error);
        }
        
        onBlur?.(e);
    };

    React.useEffect(() => {
        return () => {
            registerError(inputIdRef.current, '', null);
        };
    }, [registerError]);

    const displayError = errorMessage || latin1ValidationError;

    return (
        <S.InputContainer>
            <S.InputWrapper>
                <S.Input
                    {...restProps}
                    name={name}
                    placeholder={placeholder}
                    onBlur={handleBlur}
                    // Prevent set null or undefined
                    value={_.isNil(value) ? '' : value}
                    showSuccess={!!successMessage}
                    showError={!!displayError}
                    showDelete={!!onDelete}
                />
                {onDelete && !readOnly && (
                    <S.InputPostfixButton onClick={onDelete} type="button" disabled={restProps.disabled}>
                        <Icon path={mdiClose} />
                    </S.InputPostfixButton>
                )}
                {onOption && optionPath && (
                    <S.InputInnerPostfixButton onClick={onOption} type="button" disabled={restProps.disabled}>
                        <Icon path={optionPath} />
                    </S.InputInnerPostfixButton>
                )}
            </S.InputWrapper>

            {successMessage && (
                <S.InputBottomSuccess>
                    <S.InputBottomIcon path={mdiCheckCircle} />
                    <S.InputBottomMessage>{successMessage}</S.InputBottomMessage>
                </S.InputBottomSuccess>
            )}

            {displayError && (
                <S.InputBottomError>
                    <S.InputBottomIcon path={mdiAlertCircle} />
                    <S.InputBottomMessage>{displayError}</S.InputBottomMessage>
                </S.InputBottomError>
            )}
        </S.InputContainer>
    );
};
